import { ActivatedRoute, Params } from '@angular/router';
import { EMPTY, Observable } from 'rxjs';
import { MaintainService } from '../maintain.service';
import {
  ActionConfigDTO,
  FieldConfigDTO,
  FormRowConfigDTO,
  MaintainTabDTO,
  MenuDTO,
  MenuType,
} from '../../../../system/system-dtos';
import { PageRequestDTO, PageResponseDTO } from '../../../../common/paging-dto';
import { ExtractedActions } from '../../../../common/event-dtos';
import { DataSource, PAGINATION_MODE, SpecificEndpointDataSource } from './data-source';
import { MaintainDataSource } from './maintain-data-source';
import { QueryDataSource } from './query-data-source';
import { CassandraDataSource } from './cassandra-data-source';
import { notDefined } from '../../../utils/utils';

export class EntityGroupDataSource extends DataSource {
  public constructor(
    private route: ActivatedRoute,
    private maintainService: MaintainService,
    private maintainDataSource: MaintainDataSource,
    private queryDataSource: QueryDataSource,
    private cassandraDataSource: CassandraDataSource
  ) {
    super();
  }

  public getTabs(menuConfig: MenuDTO): MaintainTabDTO[] {
    switch (menuConfig.entityGroup.dataSources[menuConfig.entityGroup.currentMode].type) {
      case MenuType.MAINTAIN:
        return this.maintainDataSource.getTabs(menuConfig);
      case MenuType.ELASTIC:
        return this.queryDataSource.getTabs(menuConfig);
      case MenuType.CASSANDRA:
        return this.cassandraDataSource.getTabs(menuConfig);
      default:
        return null;
    }
  }

  public getRows(menuConfig: MenuDTO): FormRowConfigDTO[] {
    switch (menuConfig.entityGroup.dataSources[menuConfig.entityGroup.currentMode].type) {
      case MenuType.MAINTAIN:
        return this.maintainDataSource.getRows(menuConfig);
      case MenuType.ELASTIC:
        return this.queryDataSource.getRows(menuConfig);
      case MenuType.CASSANDRA:
        return this.cassandraDataSource.getRows(menuConfig);
      default:
        return null;
    }
  }

  doGetData(
    menu: MenuDTO,
    currentTab: MaintainTabDTO,
    pageRequest: PageRequestDTO,
    filters: { [p: string]: FieldConfigDTO },
    id: any,
    context: any,
    queryParams: Params
  ): Observable<PageResponseDTO<any>> {
    switch (menu.entityGroup.dataSources[menu.entityGroup.currentMode].type) {
      case MenuType.MAINTAIN:
        return this.maintainDataSource.doGetData(menu, currentTab, pageRequest, filters, id, context);
      case MenuType.ELASTIC:
        return this.queryDataSource.doGetData(menu, currentTab, pageRequest, filters, id, context, queryParams);
      case MenuType.CASSANDRA:
        return this.cassandraDataSource.doGetData(menu, currentTab, pageRequest, filters, id, context, queryParams);
      default:
        return null;
    }
  }

  extractActions(menuConfig: MenuDTO, tab: MaintainTabDTO, element: any): ExtractedActions {
    let elasticActions: ExtractedActions;
    if (menuConfig.entityGroup.dataSources[menuConfig.entityGroup.currentMode].type === MenuType.ELASTIC) {
      elasticActions = this.queryDataSource.extractActions(menuConfig, tab, element);
    }
    if (menuConfig.entityGroup.dataSources[menuConfig.entityGroup.currentMode].type === MenuType.CASSANDRA) {
      elasticActions = this.cassandraDataSource.extractActions(menuConfig, tab, element);
    }

    let maintainActions: ExtractedActions;
    if (menuConfig.entityGroup.dataSources[menuConfig.entityGroup.currentMode].type === MenuType.MAINTAIN) {
      maintainActions = this.maintainDataSource.extractActions(menuConfig, tab, element);
    }

    if (notDefined(tab.tableView.actions)) {
      return {
        rest: [
          ...(elasticActions ? elasticActions.rest || [] : []),
          ...(maintainActions ? maintainActions.rest || [] : []),
        ],
      };
    }
    const simple = tab.tableView.actions.map((a) => ({
      label: SpecificEndpointDataSource.parseTag(a.tag),
      command: a.tag,
    }));
    return {
      simple,
      rest: [
        ...(elasticActions ? elasticActions.rest || [] : []),
        ...(maintainActions ? maintainActions.rest || [] : []),
      ],
    };
  }

  getTabsWithCount(menuConfig: MenuDTO): Observable<{ [p: string]: number }> {
    return EMPTY;
  }

  getTitle(menuConfig: MenuDTO): string {
    return menuConfig.label;
  }

  override getNoteForData(menu: MenuDTO): string {
    switch (menu.entityGroup.dataSources[menu.entityGroup.currentMode].type) {
      case MenuType.MAINTAIN:
        return this.maintainDataSource.getNoteForData(menu);
      case MenuType.ELASTIC:
        return this.queryDataSource.getNoteForData(menu);
      case MenuType.CASSANDRA:
        return this.cassandraDataSource.getNoteForData(menu);
      default:
        return null;
    }
  }

  override extractRowData(element: any, menu: MenuDTO) {
    switch (menu.entityGroup.dataSources[menu.entityGroup.currentMode].type) {
      case MenuType.MAINTAIN:
        return this.maintainDataSource.extractRowData(element, menu);
      case MenuType.ELASTIC:
        return this.queryDataSource.extractRowData(element, menu);
      case MenuType.CASSANDRA:
        return this.cassandraDataSource.extractRowData(element, menu);
    }
  }

  override getPageMode(menu: MenuDTO): PAGINATION_MODE {
    switch (menu.entityGroup.dataSources[menu.entityGroup.currentMode].type) {
      case MenuType.MAINTAIN:
        return this.maintainDataSource.getPageMode(menu);
      case MenuType.ELASTIC:
        return this.queryDataSource.getPageMode(menu);
      case MenuType.CASSANDRA:
        return this.cassandraDataSource.getPageMode(menu);
      default:
        return null;
    }
  }

  getOverallRestActions(menu: MenuDTO): ActionConfigDTO[] {
    switch (menu.entityGroup.dataSources[menu.entityGroup.currentMode].type) {
      case MenuType.MAINTAIN:
        return this.maintainDataSource.getOverallRestActions(menu);
      case MenuType.ELASTIC:
        return this.queryDataSource.getOverallRestActions(menu);
      case MenuType.CASSANDRA:
        return this.cassandraDataSource.getOverallRestActions(menu);
      default:
        return null;
    }
  }
}
